import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface FAQItem {
  question: string;
  answer: string;
}

interface FAQProps {
  context?: 'home' | 'services' | 'about' | 'locations';
}

const faqData: Record<string, FAQItem[]> = {
  home: [
    {
      question: 'Who provides custom signs near Arlington, TX?',
      answer:
        'Signarama Arlington at 2400 W Pioneer Pkwy #118 is your local expert for custom business signage in Arlington, TX. We offer vehicle wraps, storefront signs, banners, and trade show displays. Call 817-860-9310 for a same-day free consultation with our experienced signage team.',
    },
    {
      question: 'How much does a vehicle wrap cost in Arlington?',
      answer:
        'Vehicle wrap costs in Arlington typically range from $1,500 for partial wraps to $3,500–$5,000 for full wraps, depending on vehicle size, design complexity, and material grade. Signarama Arlington offers free quotes and competitive pricing. Contact us at 817-860-9310 for your personalized estimate.',
    },
    {
      question: 'How long does it take to make a custom sign?',
      answer:
        'Most custom sign orders at Signarama Arlington are completed in 3–7 business days. Rush same-week production is available for qualifying projects. Complex installations like illuminated channel letters may take 2–3 weeks including permit coordination. We always communicate timelines upfront.',
    },
    {
      question: 'What is the best type of sign for a business storefront?',
      answer:
        'The most effective storefront signs are illuminated channel letters or cabinet signs for 24/7 visibility. For a modern premium look, dimensional letters with LED backlighting are excellent. The right choice depends on your budget, brand, and lease terms — Signarama Arlington provides free expert guidance.',
    },
    {
      question: 'Does Signarama Arlington handle sign installation?',
      answer:
        'Yes, Signarama Arlington provides professional installation for all signage types. Our certified installers handle storefront signs, vehicle wraps, trade show displays, and wayfinding systems across the DFW Metroplex. We ensure code compliance, clean finishes, and zero hassle for our clients.',
    },
    {
      question: 'What areas near Arlington does Signarama serve?',
      answer:
        'Signarama Arlington serves the entire DFW Metroplex including Grand Prairie, Mansfield, Fort Worth, Irving, Euless, Bedford, Hurst, Kennedale, and surrounding communities across Tarrant and Dallas County. On-site consultations and installations are available throughout the region.',
    },
  ],
  services: [
    {
      question: 'What types of vehicle wraps does Signarama Arlington offer?',
      answer:
        'We offer full vehicle wraps, partial wraps, spot graphics, fleet graphics, color change wraps, and commercial lettering. All wraps use premium 3M and Avery Dennison vinyl with lamination for durability. We handle cars, trucks, vans, trailers, buses, and specialty vehicles of all sizes.',
    },
    {
      question: 'Can Signarama design my sign from scratch?',
      answer:
        'Absolutely. Our in-house design team creates custom signage from concept to completion. We work from your brand guidelines or develop a fresh identity. You receive design proofs before production with unlimited revision rounds until you\'re 100% satisfied. No design experience required on your end.',
    },
    {
      question: 'What is the difference between channel letters and cabinet signs?',
      answer:
        'Channel letters are individually crafted 3D letters mounted on your building — sleek, modern, and highly visible. Cabinet signs are enclosed illuminated boxes with your graphics inside — more affordable and great for strip mall locations. Signarama Arlington helps you choose the right option for your space.',
    },
    {
      question: 'Do you offer ADA-compliant signage?',
      answer:
        'Yes, Signarama Arlington produces ADA-compliant interior and exterior signs including Braille signage, tactile lettering, room identification signs, and directional wayfinding systems. We ensure all signage meets ADA standards and local building codes for commercial properties and public spaces.',
    },
    {
      question: 'What materials are used for outdoor signage?',
      answer:
        'Outdoor signs use UV-resistant inks and weather-rated substrates including aluminum composite panels, HDU foam, acrylic, polycarbonate, and PVC. All outdoor materials are selected for durability in Texas heat and weather conditions, ensuring your signs look great for years.',
    },
    {
      question: 'Can I get a rush order on banners or event signage?',
      answer:
        'Yes, rush production is available for banners, pop-up displays, and event signage at Signarama Arlington. Standard banners can often be produced within 24–48 hours for qualifying rush orders. Contact us at 817-860-9310 to discuss your deadline and we\'ll do everything possible to meet it.',
    },
  ],
  about: [
    {
      question: 'How long has Signarama been in business?',
      answer:
        'Signarama was founded in 1986 and has grown to 900+ locations worldwide over 40 years, making it the world\'s largest sign franchise. The Arlington, TX location carries this legacy of expertise and proven systems, bringing global resources and innovation to every local business project.',
    },
    {
      question: 'Is Signarama Arlington locally owned?',
      answer:
        'Yes, Signarama Arlington is locally owned and operated by entrepreneurs with strong ties to the Arlington community. As a franchise, we combine the power of a global brand network with the personal attention and accountability of a local business owner who genuinely cares about your results.',
    },
    {
      question: 'What makes Signarama Arlington different from other sign shops?',
      answer:
        'We offer a complete end-to-end service: design, production, and installation under one roof. Our team uses premium materials, state-of-the-art equipment, and 40+ years of Signarama systems to ensure consistent, high-quality results. We also provide free design consultations and fast turnarounds.',
    },
    {
      question: 'Does Signarama Arlington offer warranties on signage?',
      answer:
        'Yes, we stand behind our work. Signarama Arlington offers material and workmanship warranties on signage products. Vehicle wraps typically carry a 2-year installation warranty. Illuminated signs carry manufacturer warranties on components. Ask our team for specific warranty terms on your project.',
    },
    {
      question: 'What is the Signarama design consultation process like?',
      answer:
        'Our free consultation begins with a discovery conversation about your goals, brand, and budget. We then provide concept designs within 48 hours. You review and approve proof designs before production begins. The entire process — from first call to installation — is guided by a dedicated project coordinator.',
    },
    {
      question: 'Can Signarama Arlington handle large commercial sign projects?',
      answer:
        'Yes, we handle large-scale commercial projects including multi-location branding programs, interior environmental graphics, wayfinding systems, and full building signage packages. Our Signarama network gives us access to specialized production capabilities for any size project.',
    },
  ],
  locations: [
    {
      question: 'Where is Signarama Arlington located?',
      answer:
        'Signarama Arlington is located at 2400 W Pioneer Pkwy, Suite 118, Arlington, Texas 76013. We are centrally located in Arlington between I-20 and Highway 360, easily accessible from throughout the DFW Metroplex. Free on-site parking is available. Call 817-860-9310 for directions.',
    },
    {
      question: 'What are Signarama Arlington\'s hours of operation?',
      answer:
        'We are open Monday through Friday from 8:00 AM to 6:00 PM. We are closed on weekends and major holidays. Walk-in consultations are welcome during business hours. You can also request a quote online 24/7 and our team will respond within one business day.',
    },
    {
      question: 'Does Signarama Arlington offer on-site consultations?',
      answer:
        'Yes, we offer on-site consultations throughout the DFW Metroplex. Our team will visit your location to take measurements, assess signage placement, review your space, and recommend the best signage solutions. On-site visits are free of charge within our primary service area.',
    },
    {
      question: 'Is there parking available at Signarama Arlington?',
      answer:
        'Yes, ample free parking is available at our Arlington location at 2400 W Pioneer Pkwy #118. We are located in a commercial center with easy access from Pioneer Parkway. Large vehicle and fleet parking for wrap consultations is also available on-site.',
    },
    {
      question: 'How far does Signarama Arlington travel for installations?',
      answer:
        'We regularly install throughout the DFW Metroplex including Fort Worth, Grand Prairie, Mansfield, Irving, and surrounding cities. For larger commercial projects, we can coordinate installations statewide. Contact us at 817-860-9310 to discuss your location and installation requirements.',
    },
    {
      question: 'Can I get a quote without visiting the Signarama Arlington store?',
      answer:
        'Absolutely. You can request a free quote online through our website form, by emailing our team, or by calling 817-860-9310. For vehicle wraps, you can send photos of your vehicle. For facility signage, photos and measurements are helpful. We make the quoting process as convenient as possible.',
    },
  ],
};

function FAQItem({ item, index }: { item: FAQItem; index: number }) {
  const [open, setOpen] = useState(false);
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay: index * 0.05 }}
      className="border border-gray-200 rounded-xl overflow-hidden"
    >
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-6 py-5 text-left hover:bg-gray-50 transition-colors cursor-pointer"
      >
        <span className="font-semibold text-gray-900 text-sm pr-4">{item.question}</span>
        <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
          <i
            className={`${open ? 'ri-subtract-line' : 'ri-add-line'} text-teal-600 text-lg transition-transform`}
          ></i>
        </div>
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden"
          >
            <div className="px-6 pb-5 pt-0">
              <p className="text-gray-600 text-sm leading-relaxed">{item.answer}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export function FAQ({ context = 'home' }: FAQProps) {
  const items = faqData[context] ?? faqData.home;

  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-teal-600 text-sm font-semibold tracking-widest uppercase mb-3 block">
            Voice Search &amp; AI Ready
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Frequently Asked{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-600 to-orange-500">
              Questions
            </span>
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Honest, direct answers to the questions we hear most from Arlington area businesses.
          </p>
        </motion.div>

        <div className="max-w-4xl mx-auto space-y-3">
          {items.map((item, index) => (
            <FAQItem key={index} item={item} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
}
